import { escapeFieldName } from "../objectPaths.js";
import { isArrayField } from "../typeGuards.js";
import { getStateValue } from "./helpers.js";
import { createBlankState } from "./initialState.js";
import { updateStateFromPrevious } from "./stateUpdating.js";
/**
 * Adds a new item to an array field.
 *
 * @template T The type of the form.
 * @param formStateTuple The form state and its setter.
 * @param field The field within the form schema that needs the field added.
 * @param adjustedPath The path to the field if not the field's default path.
 *   Typically for nested arrays.
 * @throws Error If the form state doesn't have an array at the field in
 *   question.
 */
export function addArrayItem(formStateTuple, field, adjustedPath) {
    const pathToUse = adjustedPath || field.path;
    const nestedState = getStateValue(pathToUse, formStateTuple[0]);
    if (!Array.isArray(nestedState))
        throw new Error(`The state of ${pathToUse} should be an array.`);
    updateStateFromPrevious(formStateTuple[1], pathToUse, [
        ...nestedState,
        createBlankState(field.children),
    ]);
}
/**
 * Removes an item from an array in state
 *
 * @template T The type of the form.
 * @param formStateTuple The form state and its setter.
 * @param path The path of the item to remove from.
 * @param index The index of the item in the array to remove.
 * @throws Error If the form state doesn't have an array at the field in
 *   question.
 */
export function removeArrayItem(formStateTuple, path, index) {
    const nestedState = getStateValue(path, formStateTuple[0]);
    if (!Array.isArray(nestedState))
        throw new Error(`The state of ${path} should be an array.`);
    if (nestedState[index] === undefined)
        throw new Error(`${index} is not a valid index for the array at "${path}"`);
    updateStateFromPrevious(formStateTuple[1], path, [
        ...nestedState.slice(0, index),
        ...nestedState.slice(index + 1),
    ]);
}
/**
 * Creates the path for an item in an array field.
 *
 * @param fieldDef The field to generate the path for.
 * @param arrayIndex The indices of the item in the array.
 * @returns The path with array notation.
 */
export function getArrayItemPath(fieldDef, arrayIndex) {
    let index = arrayIndex.length - 1;
    let current = fieldDef;
    let output = escapeFieldName(fieldDef.field);
    while (current.parent) {
        current = current.parent;
        if (isArrayField(current)) {
            output = "[" + arrayIndex[index] + "]." + output;
            index--;
        }
        else {
            output = "." + output;
        }
        output = escapeFieldName(current.field) + output;
    }
    return output;
}
/**
 * Gets the state value of an array field.
 *
 * @param field The array field to get the sttate for.
 * @param formState The full form state.
 * @param arrayIndex The index of the item to get the state for.
 * @returns The form states for the array field children at the provided index.
 * @throws Error If the form state at the given path is not an array.
 */
export function getArrayStateValue(field, formState, arrayIndex) {
    const workingPath = getArrayItemPath(field, arrayIndex);
    const nestedState = getStateValue(workingPath, formState);
    if (!Array.isArray(nestedState))
        throw new Error(`The state of ${workingPath} should be an array.`);
    return nestedState;
}
//# sourceMappingURL=arrayFieldHelpers.js.map